import { Fuel, Droplets, Layers, Package, Users, ClipboardCheck } from "lucide-react";

const HUB_TYPES = {
  refueling: {
    id: "refueling",
    label: "Refueling",
    description: "Diesel & AdBlue fuel supply management",
    icon: Fuel,
    color: "lime",
    item_a: { label: "Diesel", unit: "L" },
    item_b: { label: "AdBlue", unit: "L", enabled: true },
    item_c: { label: "", unit: "", enabled: false },
    level: { label: "Tank Level", unit: "%" },
    request_label: "Fuel Request",
    delivery_label: "Fuel Delivery",
    request_type_primary: "FUEL",
    delivery_verb: "Deliver",
    tier: "free",
    max_items: 2,
    editable_items: true,
  },
  liquids: {
    id: "liquids",
    label: "Liquids",
    description: "Water, concrete, or chemical liquid supply",
    icon: Droplets,
    color: "blue",
    item_a: { label: "Water", unit: "L" },
    item_b: { label: "Additive", unit: "L", enabled: true },
    item_c: { label: "", unit: "", enabled: false },
    level: { label: "Tank Level", unit: "%" },
    request_label: "Liquid Request",
    delivery_label: "Liquid Delivery",
    request_type_primary: "SUPPLY",
    delivery_verb: "Deliver",
    tier: "basic",
    max_items: 3,
    editable_items: true,
  },
  bulk: {
    id: "bulk",
    label: "Bulk Materials",
    description: "Gravel, sand, earth, and aggregate supply",
    icon: Layers,
    color: "amber",
    item_a: { label: "Primary Material", unit: "t" },
    item_b: { label: "Secondary Material", unit: "t", enabled: true },
    item_c: { label: "Tertiary Material", unit: "t", enabled: false },
    level: { label: "Stock Level", unit: "%" },
    request_label: "Material Request",
    delivery_label: "Material Delivery",
    request_type_primary: "SUPPLY",
    delivery_verb: "Deliver",
    tier: "basic",
    max_items: 3,
    editable_items: true,
  },
  colli: {
    id: "colli",
    label: "Equipment & Materials",
    description: "Construction materials, equipment, and packaged goods",
    icon: Package,
    color: "purple",
    item_a: { label: "Equipment", unit: "pcs" },
    item_b: { label: "Packaging", unit: "pallets", enabled: true },
    item_c: { label: "", unit: "", enabled: false },
    level: { label: "Inventory", unit: "%" },
    request_label: "Equipment Request",
    delivery_label: "Equipment Delivery",
    request_type_primary: "SUPPLY",
    delivery_verb: "Deliver",
    tier: "basic",
    max_items: 3,
    editable_items: true,
  },
  actions: {
    id: "actions",
    label: "Action Hub",
    description: "On-site services: cleaning, coating, watering, custom tasks",
    icon: ClipboardCheck,
    color: "orange",
    item_a: { label: "Task", unit: "action" },
    item_b: { label: "", unit: "", enabled: false },
    item_c: { label: "", unit: "", enabled: false },
    level: { label: "Completion", unit: "%" },
    request_label: "Task Request",
    delivery_label: "Task Completion",
    request_type_primary: "TASK",
    delivery_verb: "Complete",
    tier: "basic",
    max_items: 1,
    editable_items: false,
    is_task_based: true,
    default_actions: ["Cleaning", "Apply Emulsion Coating", "Water Plants", "Site Inspection", "Waste Removal"],
  },
  persons: {
    id: "persons",
    label: "Personnel",
    description: "Crew transport and personnel logistics",
    icon: Users,
    color: "teal",
    item_a: { label: "Crew Members", unit: "persons" },
    item_b: { label: "", unit: "", enabled: false },
    item_c: { label: "", unit: "", enabled: false },
    level: { label: "Headcount", unit: "" },
    request_label: "Transport Request",
    delivery_label: "Transport Confirmation",
    request_type_primary: "SUPPLY",
    delivery_verb: "Confirm",
    tier: "premium",
    max_items: 1,
    editable_items: false,
  },
};

// Role configurations for hub wizard
export const ROLE_CONFIGS = [
  {
    id: "full",
    label: "Full Crew",
    description: "Machinist requests, Planner manages, Driver delivers",
    roles: ["planner", "machinist", "driver"],
    youRoles: ["planner"],
    inviteRoles: ["driver", "machinist"],
    min_occupancy: 3,
    comboTag: "1+2",
  },
  {
    id: "planner_driver",
    label: "Planner + Driver",
    description: "You plan and deliver. Invite Machinists for requests.",
    roles: ["planner", "driver"],
    youRoles: ["planner", "driver"],
    inviteRoles: ["machinist"],
    min_occupancy: 2,
    comboTag: "2+1",
  },
  {
    id: "planner_machinist",
    label: "Planner + Machinist",
    description: "You plan and create requests on site. Invite Drivers to deliver.",
    roles: ["planner", "machinist"],
    youRoles: ["planner", "machinist"],
    inviteRoles: ["driver"],
    min_occupancy: 2,
    comboTag: "2+1",
  },
  {
    id: "solo",
    label: "Solo Operator",
    description: "Planner handles everything — requests, management, delivery",
    roles: ["planner"],
    youRoles: ["planner"],
    inviteRoles: [],
    min_occupancy: 1,
    comboTag: "1",
  },
];

export function getHubConfig(hubType) {
  return HUB_TYPES[hubType] || HUB_TYPES.refueling;
}

export function getAvailableTypes(tier) {
  const tierOrder = { free: 0, basic: 1, premium: 2 };
  const userTier = tierOrder[tier] ?? 0;
  return Object.values(HUB_TYPES).map(t => ({
    ...t,
    locked: tierOrder[t.tier] > userTier,
  }));
}

export default HUB_TYPES;
